import { Component, OnInit, ViewChild } from '@angular/core';
import { TableConfig } from "@app/shared/util/table-config.utils";
import { ToolReturnsService } from "@services/tool-returns.service";
import { TranslateService } from "@ngx-translate/core";
import { Log } from "ng2-logger";
import { ToolRequestsService } from "@services/tool-requests.service";
import { ToolRequest } from "@models/toolRequests.model";
import { ConfirmationModalComponent } from '@app/shared/components';
import { ToolRequestStatus } from "@app/tool/model/enums/tool-request-status.enum";
import { Subscription } from "rxjs";
import { NotificationService } from "@services/notification.service";
import { Section } from "@models/enums/section.utils";
import { PendingToolRequestsModalComponent } from '@app/shared/pages/pending-tool-requests-modal/pending-tool-requests-modal.component';
import { LogoutModalComponent } from '@app/shared/components';
import { environment } from '../../../../../environments/environment';

@Component({
    selector: 'app-pending-tool-requests',
    templateUrl: './pending-tool-requests.component.html',
    styleUrls: ['./pending-tool-requests.component.scss']
})
export class PendingToolRequestsComponent implements OnInit {

    _logger = Log.create(PendingToolRequestsComponent.name);

    @ViewChild('errorModal', {static: true}) errorModal: ConfirmationModalComponent;
    @ViewChild(PendingToolRequestsModalComponent, {static: true}) pendingToolRequestsModal: PendingToolRequestsModalComponent;
    @ViewChild(LogoutModalComponent, {static: false}) logoutModal: LogoutModalComponent;

    pendingToolRequests: ToolRequest[] = [];
    tableConfig = new TableConfig();
    isLoading = false;

    confirmationPopupMessage: string;
    filterStatusList = [];
    isConfirmationInProgress = false;

    toolRequestQueueSubscription: Subscription;

    showFilters = true;
    readonly badgeLogoutCountdownSeconds = environment.badgeLogoutCountdownSeconds;

    constructor(private toolReturnsService: ToolReturnsService,
                private toolRequestsService: ToolRequestsService,
                private notificationService: NotificationService,
                private translationService: TranslateService) {

        this.filterStatusList = this.buildStatusFilterColumnKeys();

        this.translationService.onLangChange.subscribe((event) => {
            this.filterStatusList = this.buildStatusFilterColumnKeys();
        });

    }

    ngOnInit() {
        this.getPendingToolRequests(this.tableConfig);
        this.toolRequestQueueSubscription = this.notificationService.lineSetterToolRequestQueueAsObservable.subscribe((value) => {
            if (value) {
                this._logger.info("ngOnInit:: toolRequestQueueSubscription got value", value);
                this.getPendingToolRequests(this.tableConfig);
            }
        });
    }

    getPendingToolRequests(tableConfig = this.tableConfig) {
        this._logger.info('getPendingToolReturns:: Retrieving pending tool returns ');
        this.isLoading = true;
        this.toolRequestsService.getAllToolRequests(tableConfig, 'LINE', Section.ViewToolRequests).subscribe(
            (data) => {
                this._logger.info('getPendingToolReturns:: API Returned data', data);
                this.pendingToolRequests = data.items;
                this.tableConfig.pager.count = data.total;
                this.isLoading = false;
            },
            (error) => {
                this._logger.error('getPendingToolReturns:: API Returned error', error);
                this.isLoading = false;
            }
        )
    }

    onTableRowClick(toolRequest: ToolRequest) {
        this.toolRequestsService.getToolRequestDetails(toolRequest.id).subscribe(
            (data) => {
                this._logger.info('onTableRowClick:: API Returned data', data);
                this.pendingToolRequestsModal.open(data);
            },
            (error) => {
                this._logger.info('onTableRowClick:: API Returned error', error);
            }
        );
    }


    buildCancelMessage(pendingToolRequestName) {
        return this.translationService.instant('lineSetter.pending-tool-requests.table.toasts.closed',
            {pendingToolRequestName: pendingToolRequestName});
    }

    onSortChange(tableState) {
        this.getPendingToolRequests(tableState);
    }

    onFilter(tableConfig: TableConfig) {
        this.getPendingToolRequests(tableConfig);
    }

    onPageChange(tableConfig: TableConfig) {
        this.getPendingToolRequests(tableConfig);
    }

    onClose(toolRequest: ToolRequest) {
        this.cancelToolRequest(toolRequest);
    }

    buildStatusFilterColumnKeys() {
        return [
            {name: this.translationService.instant("generic.statuses.all"), value: 'All'},
            {
                name: this.translationService.instant("generic.statuses.new"),
                value: ToolRequestStatus.NEW
            },
            {
                name: this.translationService.instant("generic.statuses.inTransport"),
                value: ToolRequestStatus.IN_TRANSPORT
            },
            {
                name: this.translationService.instant("generic.statuses.approved"),
                value: ToolRequestStatus.APPROVED
            },
            {
                name: this.translationService.instant("generic.statuses.rejected"),
                value: ToolRequestStatus.REJECTED
            },
            {
                name: this.translationService.instant("generic.statuses.pending"),
                value: ToolRequestStatus.WAITING_FOR_APPROVAL
            },
            {
                name: this.translationService.instant("generic.statuses.closed"),
                value: ToolRequestStatus.CLOSED
            },
            {
                name: this.translationService.instant("generic.statuses.canceled"),
                value: ToolRequestStatus.CANCELED
            }
        ];
    }

    cancelToolRequest(toolRequest) {
        this.confirmationPopupMessage = '';
        this._logger.info('onClose:: Closing Tool Return request', toolRequest);
        this.isConfirmationInProgress = true;
        this.toolRequestsService.cancelToolReturnRequest(toolRequest).subscribe(
            (data) => {
                this._logger.info('onClose:: API Returned data', data);
                this.getPendingToolRequests();
                this.confirmationPopupMessage = this.buildCancelMessage(toolRequest.name);
                this.pendingToolRequestsModal.closeConfirmationModal();
                this.isConfirmationInProgress = false;
                this.logoutModal.open();
            },
            (error) => {
                this.isConfirmationInProgress = false;
                this.pendingToolRequestsModal.closeConfirmationModal();
                if (error && error.error) {
                    if (error.error.message !== 'errorMessage.401.invalidBoschId') {
                        this.confirmationPopupMessage = this.translationService.instant(error.error.message);
                        this.errorModal.open();
                    }
                }
                this._logger.error('onClose:: API Returned error', error);
            }
        )
    }

    toggleFilters() {
        this.showFilters = !this.showFilters;
    }
}
